import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import { Plus, Trash2, Send, Save, Ban, ArrowLeft, AlertCircle, Truck, Printer } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { DocstatusPill, StatusPill } from '@/components/StatusPill';
import { Timeline } from '@/components/Timeline';
import { Kbd } from '@/components/Kbd';
import { api, apiBlob } from '@/lib/api';
import { money } from '@/lib/format';
import { toast } from '@/components/Toaster';

interface Customer  { id: string; name: string; display_name: string }
interface Item      { id: string; code: string; name: string; stock_uom: string; standard_rate: string; is_stock_item?: boolean; interchange_no?: string }
interface Warehouse { id: string; code: string; name: string; is_group?: boolean }
interface Project   { id: string; name: string; project_name: string }
interface SalesOrder { id: string; name: string; customer_id: string; status: string }

interface DNLine {
  id: string; row_index: number; item_id: string;
  item_code: string; item_name: string; description?: string;
  uom: string; qty: string; warehouse_id?: string; rate: string; amount: string;
}
interface DeliveryNote {
  id: string; name: string; company_id: string;
  customer_id: string; customer_name?: string;
  posting_date: string;
  sales_order_id?: string;
  project_id?: string;
  set_warehouse_id?: string;
  status: string;
  remarks?: string;
  docstatus: 0 | 1 | 2;
  total_value: string;
  items: DNLine[];
}

interface DraftLine {
  rowId: string;
  item_id: string;
  item_code: string;
  item_name: string;
  uom: string;
  qty: string;
  warehouse_id: string;
  rate: string;
}

const rid = () => Math.random().toString(36).slice(2);
const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyLine = (): DraftLine => ({ rowId: rid(), item_id: '', item_code: '', item_name: '', uom: 'Unit', qty: '1', warehouse_id: '', rate: '0' });

export function DeliveryNoteForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['delivery-note', id],
    enabled: !isNew,
    queryFn: () => api<DeliveryNote>(`/accounting/delivery-notes/${id}`),
  });
  const { data: customersResp }  = useQuery({ queryKey: ['customers'],    queryFn: () => api<{ items: Customer[] }>('/accounting/customers') });
  const { data: itemsResp }      = useQuery({ queryKey: ['items'],        queryFn: () => api<{ items: Item[] }>('/accounting/items') });
  const { data: warehousesResp } = useQuery({ queryKey: ['warehouses'],   queryFn: () => api<{ items: Warehouse[] }>('/stock/warehouses') });
  const { data: projectsResp }   = useQuery({ queryKey: ['projects'],     queryFn: () => api<{ items: Project[] }>('/projects/projects') });
  const { data: salesOrdersResp } = useQuery({ queryKey: ['sales-orders'], queryFn: () => api<{ items: SalesOrder[] }>('/accounting/sales-orders') });

  const customers  = customersResp?.items ?? [];
  const items      = itemsResp?.items ?? [];
  const warehouses = useMemo(() => (warehousesResp?.items ?? []).filter((w) => !w.is_group), [warehousesResp]);
  const projects   = projectsResp?.items ?? [];
  const salesOrders = salesOrdersResp?.items ?? [];

  const [customerID, setCustomerID]   = useState('');
  const [postingDate, setPostingDate] = useState(todayISO());
  const [salesOrderID, setSalesOrderID] = useState('');
  const [projectID, setProjectID]     = useState('');
  const [setWarehouseID, setSetWarehouseID] = useState('');
  const [remarks, setRemarks]         = useState('');
  const [lines, setLines]             = useState<DraftLine[]>([emptyLine()]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setCustomerID(existing.customer_id);
    setPostingDate(existing.posting_date.slice(0, 10));
    setSalesOrderID(existing.sales_order_id ?? '');
    setProjectID(existing.project_id ?? '');
    setSetWarehouseID(existing.set_warehouse_id ?? '');
    setRemarks(existing.remarks ?? '');
    setLines(existing.items.map((l) => ({
      rowId: l.id,
      item_id: l.item_id,
      item_code: l.item_code,
      item_name: l.item_name,
      uom: l.uom || 'Unit',
      qty: l.qty,
      warehouse_id: l.warehouse_id ?? '',
      rate: l.rate,
    })));
  }, [existing]);

  const editable  = isNew || existing?.docstatus === 0;
  const submitted = existing?.docstatus === 1;
  const cancelled = existing?.docstatus === 2;

  // Only submitted SOs for the chosen customer are valid links (backend rule).
  const soOptions = useMemo(() => salesOrders
    .filter((so) => so.status !== 'Cancelled' && (!customerID || so.customer_id === customerID))
    .map((so) => ({ value: so.id, label: so.name, description: so.status })), [salesOrders, customerID]);
  const customerOptions  = useMemo(() => customers.map((c) => ({ value: c.id, label: c.display_name, description: c.name })), [customers]);
  const warehouseOptions = useMemo(() => warehouses.map((w) => ({ value: w.id, label: w.name, description: w.code })), [warehouses]);

  // A delivery note ships stock OUT of a warehouse, so out-of-stock items can't
  // be delivered. Each line ships from its own warehouse (falling back to the
  // header default), so we fetch on-hand contents for every warehouse in play
  // and grey out / block items with no balance in that line's warehouse.
  const activeWhIds = useMemo(() => {
    const s = new Set<string>();
    for (const l of lines) { const wh = l.warehouse_id || setWarehouseID; if (wh) s.add(wh); }
    return Array.from(s);
  }, [lines, setWarehouseID]);
  const contentsQueries = useQueries({
    queries: activeWhIds.map((whId) => ({
      queryKey: ['wh-contents-dn', whId],
      queryFn:  () => api<{ items: { item_id: string; code: string; name: string; uom: string; qty: string }[] }>(`/stock/warehouses/${whId}/contents`),
      enabled:  !!whId && editable,
      staleTime: 30_000,
    })),
  });
  const stockByWh = useMemo(() => {
    const m = new Map<string, Map<string, { qty: string; uom: string }>>();
    activeWhIds.forEach((whId, i) => {
      const inner = new Map<string, { qty: string; uom: string }>();
      for (const it of contentsQueries[i]?.data?.items ?? []) inner.set(it.item_id, { qty: it.qty, uom: it.uom });
      m.set(whId, inner);
    });
    return m;
  }, [activeWhIds, contentsQueries]);
  const buildItemOptions = useCallback((whId: string) => {
    const stock = whId ? stockByWh.get(whId) : undefined;
    if (stock) {
      const opts = items.filter((it) => it.is_stock_item !== false).map((it) => {
        const s = stock.get(it.id);
        const qty = s ? Number(s.qty) : 0;
        const inStock = Number.isFinite(qty) && qty > 0;
        return {
          value: it.id, label: it.name,
          description: [it.code, it.interchange_no].filter(Boolean).join(' · '),
          badge: inStock ? `in stock ${s!.qty} ${s!.uom}` : 'out of stock',
          badgeMuted: !inStock,
          disabled: !inStock,
        };
      });
      return opts.sort((a, b) => Number(a.disabled) - Number(b.disabled));
    }
    // No warehouse chosen yet (— Any —): can't gate, list all items.
    return items.map((it) => ({ value: it.id, label: it.name, description: it.code }));
  }, [items, stockByWh]);
  const projectOptions   = useMemo(() => projects.map((p) => ({ value: p.id, label: p.project_name, description: p.name })), [projects]);

  const total = useMemo(() => lines.reduce(
    (a, l) => a.plus(new Decimal(l.qty || '0').times(new Decimal(l.rate || '0'))),
    new Decimal(0),
  ), [lines]);

  function addLine() { setLines((s) => [...s, emptyLine()]); }
  function patch(rowId: string, p: Partial<DraftLine>) { setLines((s) => s.map((l) => l.rowId === rowId ? { ...l, ...p } : l)); }
  function remove(rowId: string) { setLines((s) => s.length > 1 ? s.filter((l) => l.rowId !== rowId) : s); }
  function pickItem(rowId: string, itemID: string | null) {
    const it = items.find((i) => i.id === itemID);
    patch(rowId, {
      item_id: itemID ?? '',
      item_code: it?.code ?? '',
      item_name: it?.name ?? '',
      uom: it?.stock_uom ?? 'Unit',
      rate: it?.standard_rate ?? '0',
    });
  }

  function validate(): string[] {
    const out: string[] = [];
    if (!customerID)  out.push('Customer is required.');
    if (!postingDate) out.push('Posting date is required.');
    const valid = lines.filter((l) => l.item_id);
    if (valid.length === 0) out.push('Add at least one item line.');
    lines.forEach((l, i) => {
      if (!l.item_id) return;
      if (new Decimal(l.qty || '0').lte(0)) out.push(`Line ${i + 1}: qty must be > 0.`);
      if (new Decimal(l.rate || '0').lt(0)) out.push(`Line ${i + 1}: rate cannot be negative.`);
    });
    return out;
  }

  function buildBody() {
    return {
      customer_id: customerID,
      posting_date: postingDate,
      sales_order_id: salesOrderID || undefined,
      project_id: projectID || undefined,
      set_warehouse_id: setWarehouseID || undefined,
      remarks: remarks || undefined,
      items: lines.filter((l) => l.item_id).map((l) => ({
        item_id: l.item_id,
        item_code: l.item_code || undefined,
        item_name: l.item_name || undefined,
        uom: l.uom || undefined,
        qty: l.qty,
        warehouse_id: l.warehouse_id || undefined,
        rate: l.rate || undefined,
      })),
    };
  }

  const createMutation = useMutation({
    mutationFn: () => api<DeliveryNote>('/accounting/delivery-notes', { method: 'POST', body: buildBody() }),
    onSuccess: (dn) => {
      toast.success('Draft saved', dn.name);
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/delivery-notes'] });
      navigate({ to: `/accounting/delivery-notes/${dn.id}` as never });
    },
    onError: (e: { message?: string }) => { setErr(e.message ?? 'Save failed.'); toast.error('Could not save', e.message); },
  });
  const updateMutation = useMutation({
    mutationFn: () => api<DeliveryNote>(`/accounting/delivery-notes/${id}`, { method: 'PUT', body: buildBody() }),
    onSuccess: () => {
      toast.success('Saved', 'Draft updated.');
      qc.invalidateQueries({ queryKey: ['delivery-note', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/delivery-notes'] });
    },
    onError: (e: { message?: string }) => { setErr(e.message ?? 'Save failed.'); toast.error('Save failed', e.message); },
  });
  const submitMutation = useMutation({
    mutationFn: () => api<DeliveryNote>(`/accounting/delivery-notes/${id}/submit`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Submitted', 'Delivered qty rolled onto the Sales Order.');
      qc.invalidateQueries({ queryKey: ['delivery-note', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/delivery-notes'] });
      qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
    onError: (e: { message?: string }) => { setErr(e.message ?? 'Submit failed.'); toast.error('Submit failed', e.message); },
  });
  const cancelMutation = useMutation({
    mutationFn: () => api<DeliveryNote>(`/accounting/delivery-notes/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.warning('Cancelled', 'Delivered qty rollup reversed.');
      qc.invalidateQueries({ queryKey: ['delivery-note', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/delivery-notes'] });
      qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
    onError: (e: { message?: string }) => { setErr(e.message ?? 'Cancel failed.'); toast.error('Cancel failed', e.message); },
  });

  async function onPrint() {
    try {
      const blob = await apiBlob(`/accounting/delivery-notes/${id}/print`);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { toast.error('Cetak SPB gagal', (e as { message?: string }).message); }
  }

  function onSaveDraft() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    createMutation.mutate();
  }
  function onSave() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    updateMutation.mutate();
  }

  const title = isNew ? 'New Delivery Note' : (existing?.name ?? '…');

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Sales', to: '/accounting' },
          { label: 'Delivery Notes', to: '/accounting/delivery-notes' },
          { label: isNew ? 'New' : title },
        ]}
        title={title}
        status={existing && (
          <div className="flex items-center gap-2">
            <DocstatusPill docstatus={existing.docstatus} />
            <DNStatusPill value={existing.status} />
          </div>
        )}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/accounting/delivery-notes' as never}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            {editable && isNew && (
              <Button onClick={onSaveDraft} loading={createMutation.isPending}>
                <Save className="size-4" /> Save draft <Kbd>⌘S</Kbd>
              </Button>
            )}
            {editable && !isNew && (
              <Button variant="secondary" onClick={onSave} loading={updateMutation.isPending}>
                <Save className="size-4" /> Save
              </Button>
            )}
            {editable && !isNew && (
              <Button onClick={() => submitMutation.mutate()} loading={submitMutation.isPending}>
                <Send className="size-4" /> Submit
              </Button>
            )}
            {submitted && (
              <Button variant="secondary" onClick={onPrint}>
                <Printer className="size-4" /> Print SPB
              </Button>
            )}
            {submitted && (
              <Button variant="danger" onClick={() => cancelMutation.mutate()} loading={cancelMutation.isPending}>
                <Ban className="size-4" /> Cancel
              </Button>
            )}
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4 max-w-[1400px]"
      >
        <div className="space-y-4 min-w-0">
          {cancelled && (
            <Card className="!p-3 flex items-center gap-3 bg-danger/5 border-danger/30">
              <AlertCircle className="size-4 text-danger" />
              <span className="text-body text-danger">This delivery note has been cancelled. The delivered-qty rollup was reversed.</span>
            </Card>
          )}
          {err && (
            <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
              <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
              <div className="text-body-sm text-brand-error">{err}</div>
            </Card>
          )}

          <Card>
            <CardTitle>Delivery to</CardTitle>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Field label="Customer" hint="Required.">
                <Combobox options={customerOptions} value={customerID || null}
                  onChange={(v) => { setCustomerID(v ?? ''); if (salesOrderID) setSalesOrderID(''); }}
                  placeholder="Pick a customer…" disabled={!editable || !!salesOrderID} />
              </Field>
              <Field label="Posting date">
                <Input type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} disabled={!editable} />
              </Field>
              <Field label="Against Sales Order" hint="Optional — links delivered qty back to the SO. SO must be submitted.">
                <Combobox options={soOptions} value={salesOrderID || null}
                  onChange={(v) => {
                    setSalesOrderID(v ?? '');
                    const so = salesOrders.find((s) => s.id === v);
                    if (so) setCustomerID(so.customer_id);
                  }}
                  placeholder="— None —" clearable disabled={!editable} />
              </Field>
              <Field label="Project" hint="Optional cost object.">
                <Combobox options={projectOptions} value={projectID || null} onChange={(v) => setProjectID(v ?? '')} placeholder="— None —" clearable disabled={!editable} />
              </Field>
              <Field label="Default warehouse" hint="Optional — source warehouse for the shipment.">
                <Combobox options={warehouseOptions} value={setWarehouseID || null} onChange={(v) => setSetWarehouseID(v ?? '')} placeholder="— None —" clearable disabled={!editable} />
              </Field>
            </div>
          </Card>

          <Card padded={false}>
            <div className="flex items-center justify-between p-5 pb-3">
              <CardTitle>Items</CardTitle>
              {editable && <Button variant="ghost" size="sm" onClick={addLine}><Plus className="size-4" /> Add line</Button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                  <tr>
                    <th className="text-left font-medium px-3 py-2 w-10">#</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[220px]">Item</th>
                    <th className="text-right font-medium px-3 py-2 w-24">Qty</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">Warehouse</th>
                    <th className="text-right font-medium px-3 py-2 w-32">Rate</th>
                    <th className="text-right font-medium px-3 py-2 w-32">Amount</th>
                    {editable && <th className="w-10" />}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const amt = new Decimal(l.qty || '0').times(new Decimal(l.rate || '0'));
                    return (
                      <tr key={l.rowId} className="border-t border-hairline">
                        <td className="px-3 py-2 text-stone">{idx + 1}</td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? (() => {
                                // Guarantee the loaded line's item is a selectable
                                // option even when it's absent from the warehouse's
                                // in-stock list — otherwise the combobox can't resolve
                                // its label and falls back to the "Pick item…" placeholder.
                                const opts = buildItemOptions(l.warehouse_id || setWarehouseID);
                                const present = !l.item_id || opts.some((o) => o.value === l.item_id);
                                const merged = present
                                  ? opts
                                  : [{ value: l.item_id, label: l.item_name || l.item_code || l.item_id, description: l.item_code }, ...opts];
                                return <Combobox options={merged} value={l.item_id || null} onChange={(v) => pickItem(l.rowId, v)} placeholder="Pick item…" />;
                              })()
                            : <span className="px-3 text-charcoal">{l.item_name || l.item_code || l.item_id}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <NumericInput value={l.qty} onChange={(v) => patch(l.rowId, { qty: v })} decimalPlaces={0} />
                            : <span className="num text-right block px-3">{l.qty}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox options={warehouseOptions} value={l.warehouse_id || null} onChange={(v) => patch(l.rowId, { warehouse_id: v ?? '' })} placeholder="— Any —" clearable />
                            : <span className="px-3 text-charcoal">{warehouses.find((w) => w.id === l.warehouse_id)?.name || '—'}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <NumericInput value={l.rate} onChange={(v) => patch(l.rowId, { rate: v })} currencyPrefix="Rp" decimalPlaces={0} />
                            : <span className="num text-right block px-3">{money(l.rate)}</span>}
                        </td>
                        <td className="px-3 py-2 text-right num">{money(amt.toString())}</td>
                        {editable && (
                          <td className="px-2 py-1.5">
                            <Button variant="ghost" size="icon" aria-label="Remove" onClick={() => remove(l.rowId)} disabled={lines.length <= 1}>
                              <Trash2 className="size-3.5" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-hairline flex flex-wrap items-center justify-end gap-x-4 gap-y-2 num">
              <div className="text-caption text-stone">Total value</div>
              <div className="font-medium">{money(total.toString())}</div>
            </div>
          </Card>

          <Card>
            <Field label="Remarks">
              <textarea className="input-base !h-auto !py-2" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!editable} />
            </Field>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card>
            <CardTitle>Summary</CardTitle>
            <div className="mt-3 space-y-2 text-body-sm">
              <Row label="Status" pill={existing ? <DocstatusPill docstatus={existing.docstatus} /> : <StatusPill tone="neutral">Draft</StatusPill>} />
              {existing && <Row label="Delivery" pill={<DNStatusPill value={existing.status} />} />}
              <Row label="Lines" value={String(lines.filter((l) => l.item_id).length)} />
              <Row label="Total value" value={existing ? money(existing.total_value) : money(total.toString())} highlight />
            </div>
            <div className="mt-2 text-caption text-stone flex items-start gap-1.5">
              <Truck className="size-3.5 shrink-0 mt-0.5" />
              <span>Submit rolls delivered qty onto the linked Sales Order. No GL — revenue/COGS lands on the Sales Invoice.</span>
            </div>
          </Card>

          {!isNew && existing && <Timeline hideViews doctype="delivery_note" documentId={existing.id} />}
        </aside>
      </motion.div>
    </>
  );
}

function DNStatusPill({ value }: { value: string }) {
  switch (value) {
    case 'Draft':     return <StatusPill tone="neutral" withDot={false}>Draft</StatusPill>;
    case 'To Bill':   return <StatusPill tone="warning" withDot={false}>To Bill</StatusPill>;
    case 'Completed': return <StatusPill tone="success" withDot={false}>Completed</StatusPill>;
    case 'Cancelled': return <StatusPill tone="danger"  withDot={false}>Cancelled</StatusPill>;
    default:          return <StatusPill tone="neutral" withDot={false}>{value || '—'}</StatusPill>;
  }
}

function Row({ label, value, pill, highlight }: { label: string; value?: string; pill?: React.ReactNode; highlight?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-stone">{label}</span>
      {pill ?? <span className={'num text-right ' + (highlight ? 'text-ink font-semibold' : 'text-charcoal')}>{value}</span>}
    </div>
  );
}
